import { useCallback, useEffect, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { Drone } from '../../types/drone';
import {
  FLEET_FIT_OPTIONS,
  fleetBounds,
  placeFleet,
} from '../../utils/fleetViewport';

interface FleetViewportProps {
  /** The drones this view is showing, already filtered by ownership. */
  drones: Drone[];
  /**
   * Whether this map may frame itself on the fleet.
   *
   * False when the viewport was restored from where the operator left it
   * (#441): that is their choice, and snapping away from it is the same defect
   * in the other direction. They get the off-screen count and a control
   * instead.
   */
  fitToFleet: boolean;
  /** How many placeable drones are currently outside the viewport. */
  onOffScreenChange: (count: number) => void;
}

/**
 * Keeps the viewport and the fleet in agreement (#589).
 *
 * Two jobs, deliberately together, because the defect lived in the *pair* and
 * not in either half. `useInitialMapView` was correct about what it returned;
 * `unplaceableCount` was correct about drones with no coordinates. Nobody
 * asked what the operator could see, so a fleet of five drew zero markers and
 * the screen explained three of them.
 *
 *   1. Frame the map on the fleet the first time there is a fleet to frame —
 *      but only when there is no remembered viewport to destroy.
 *   2. Report, continuously, how many placeable drones are off-screen, so the
 *      notice can close the arithmetic whatever the operator does next.
 *
 * Renders nothing; must live inside a `MapContainer`.
 */
export const FleetViewport: React.FC<FleetViewportProps> = ({
  drones,
  fitToFleet,
  onOffScreenChange,
}) => {
  const map = useMap();
  const fitted = useRef(false);

  const report = useCallback(() => {
    // A map that has not been laid out reports a zero-sized viewport, and every
    // drone would look off-screen. Pass null rather than invent a notice.
    const size = map.getSize();
    const bounds = size.x > 0 && size.y > 0 ? map.getBounds() : null;
    onOffScreenChange(placeFleet(drones, bounds).offScreen.length);
  }, [drones, map, onOffScreenChange]);

  useMapEvents({ moveend: report, zoomend: report, resize: report });

  useEffect(() => {
    // "On mount" means the first time there is something to fit: the fleet is
    // fetched after this component mounts, so an empty first render must not
    // consume the one chance to frame it. `fitted` then makes sure a telemetry
    // tick never yanks the view — fitting once is a different act from fitting
    // continuously.
    if (fitToFleet && !fitted.current) {
      const bounds = fleetBounds(drones);
      if (bounds !== null) {
        fitted.current = true;
        map.fitBounds(bounds, FLEET_FIT_OPTIONS);
      }
    }
    report();
  }, [drones, fitToFleet, map, report]);

  return null;
};
